import { Button } from "@/components/ui/button";
import { Plane, Clock, Euro, Link, File, Ticket } from "lucide-react";
import { FlightActivity, ItineraryActivity } from "../models/activity.model";
import { BaseActivityCard } from "./BaseActivityCard";

interface FlightCardProps {
  activity: FlightActivity;
  onEdit: (activity: ItineraryActivity) => void;
  onDelete: (activity: ItineraryActivity) => void;
  onDuplicate?: (activity: ItineraryActivity) => void;
  onExpand?: () => void;
  isReadOnly?: boolean;
  onDragStart?: (activity: ItineraryActivity) => void;
  onDropBefore?: (sourceActivityId: string, targetActivityId?: string) => void;
  onDragEnd?: () => void;
}

export const FlightCard = ({
  activity,
  onEdit,
  onDelete,
  onDuplicate,
  onExpand,
  isReadOnly = false,
  onDragStart,
  onDropBefore,
  onDragEnd
}: FlightCardProps) => {

  const firstTravel = activity.travels?.[0];


  const renderAlwaysVisible = () => (
    <>
      {firstTravel &&
        (<>
          <div className="flex items-center gap-2">
            <Ticket className="h-4 w-4" />
            <span>{firstTravel.flightNumber}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            <span>{firstTravel.fromTime} - {firstTravel.toTime}</span>
          </div>
          <div className="flex items-center gap-2">
            <Euro className="h-4 w-4" />
            <span>{activity.cost} (only visible to you)</span>
          </div>
        </>)}
    </>
  );

  const renderCollapsible = () => (
    <>
      {/* Description */}
      {activity.description && (
        <div className="mb-4">
          <span className="text-sm font-medium mb-1 block">Note (only visible to you)</span>
          <p className="text-sm text-gray-600 leading-relaxed">{activity.description}</p>
        </div>
      )}

      {/* Flights */}
      {activity.travels && activity.travels.length > 0 && (
        <div className="mb-4">
          <span className="text-sm font-medium mb-1 block">Flight Details</span>
          <div className="space-y-2">
            {activity.travels.map((travel, index) => (
              <div key={index} className="bg-gray-50 p-3 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">{travel.flightNumber}</span>
                  <span className="text-sm text-gray-600">{travel.airline}</span>
                </div>
                <div className="text-sm text-gray-600">
                  <div>{travel.from} → {travel.to}</div>
                  <div>{travel.fromDate} {travel.fromTime} - {travel.toDate} {travel.toTime}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documentation */}
      {activity.documentation && activity.documentation.length > 0 && (
        <div className="mb-4">
          <span className="text-sm font-medium mb-2 block">Documentation</span>
          <div className="space-y-3">
            {activity.documentation.map((doc, index) => (
              <div key={index} className="flex items-center gap-3 text-sm text-gray-600">
                <File className="h-4 w-4" />
                <a
                  href={doc.url}
                  download
                  className="hover:underline cursor-pointer"
                >
                  {doc.name}
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          {/* Link Button */}
          {activity.link && (
            <Button
              variant="ghost"
              size="sm"
              className="rounded-xl"
              onClick={() => window.open(activity.link, '_blank')}
            >
              <Link className="h-5 w-5 mr-2 flex-shrink-0" />
              <span className="truncate max-w-[200px] inline-block">
                {activity.link}
              </span>
            </Button>
          )}
        </div>
      </div>
    </>
  );

  return (
    <BaseActivityCard
      activity={activity}
      onEdit={onEdit}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
      onDragStart={onDragStart}
      onDropBefore={onDropBefore}
      onDragEnd={onDragEnd}
      onExpand={onExpand}
      isReadOnly={isReadOnly}
    >
      {{
        alwaysVisible: renderAlwaysVisible(),
        collapsible: renderCollapsible()
      }}
    </BaseActivityCard>
  );
};
